import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  Clock,
  Users,
  FileText,
  CheckCircle,
  Warning,
  ArrowClockwise,
  MagnifyingGlass,
} from '@phosphor-icons/react';

export const AdminOvertimePage = () => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('current');
  
  // Current overtime state
  const [currentOvertime, setCurrentOvertime] = useState([]);
  const [loadingCurrent, setLoadingCurrent] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Snapshots state
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(true);

  useEffect(() => {
    if (activeTab === 'current') fetchCurrentOvertime();
    else if (activeTab === 'snapshots') fetchSnapshots();
  }, [activeTab]);

  const fetchCurrentOvertime = async () => {
    setLoadingCurrent(true);
    try {
      const response = await api.get('/admin/overtime/current');
      setCurrentOvertime(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch overtime data');
    } finally {
      setLoadingCurrent(false);
    }
  };

  const fetchSnapshots = async () => {
    setLoadingSnapshots(true);
    try {
      const response = await api.get('/admin/overtime/snapshots');
      setSnapshots(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch snapshots');
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,overtime_hours,last_overtime_date\njohn.doe@company.com,12.5,2026-01-15\njane.smith@company.com,8.0,2026-01-10';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'overtime_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      // Validate headers
      if (!headers.includes('email') || !headers.includes('overtime_hours')) {
        toast.error('CSV must have "email" and "overtime_hours" columns');
        return;
      }
      
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });
        rows.push(row);
      }
      
      setUploadPreview({
        file,
        headers,
        rows: rows.slice(0, 10), // Preview first 10
        totalRows: rows.length
      });
      setPreviewDialogOpen(true);
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!uploadPreview?.file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadPreview.file);
    if (uploadNotes) formData.append('notes', uploadNotes);
    
    try {
      const response = await api.post('/admin/overtime/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      toast.success(`Processed ${response.data.rows_processed} rows`);
      
      if (response.data.rows_failed > 0) {
        toast.warning(`${response.data.rows_failed} rows failed`);
      }
      
      // Refresh data
      fetchCurrentOvertime();
      fetchSnapshots();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setPreviewDialogOpen(false);
      setUploadPreview(null);
      setUploadNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleReapply = async (snapshotId) => {
    try {
      const response = await api.post(`/admin/overtime/snapshots/${snapshotId}/apply`);
      toast.success(`Re-applied to ${response.data.users_updated} users`);
      fetchCurrentOvertime();
    } catch (error) {
      toast.error('Failed to re-apply snapshot');
    }
  };

  const filteredOvertime = currentOvertime.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="admin-overtime-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">
          Overtime Management
        </h1>
        <p className="text-slate-600">
          Upload and manage overtime hours for Quick Canvass bid ranking
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="current" className="flex items-center gap-2">
            <Users size={16} />
            Current Overtime
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload size={16} />
            Upload CSV
          </TabsTrigger>
          <TabsTrigger value="snapshots" className="flex items-center gap-2">
            <FileText size={16} />
            Upload History
          </TabsTrigger>
        </TabsList>

        {/* Current Overtime Tab */}
        <TabsContent value="current">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Current Overtime List</CardTitle>
                  <CardDescription>
                    Users ranked by overtime hours (lowest first for bidding)
                  </CardDescription>
                </div>
                <div className="relative w-64">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="overtime-search"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingCurrent ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : filteredOvertime.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No overtime data found</p>
                  <p className="text-sm">Upload a CSV to populate overtime hours</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Rank</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Classification</TableHead>
                      <TableHead className="text-right">Overtime Hours</TableHead>
                      <TableHead>Last OT Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOvertime.map((user, idx) => (
                      <TableRow key={user.user_id} data-testid={`overtime-row-${user.user_id}`}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            #{idx + 1}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-slate-500">{user.email}</TableCell>
                        <TableCell>
                          {user.classification ? (
                            <Badge variant="secondary">{user.classification}</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {user.overtime_hours?.toFixed(1) || '0.0'}
                        </TableCell>
                        <TableCell>
                          {user.last_overtime_date || <span className="text-slate-400">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upload Tab */}
        <TabsContent value="upload">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Upload Overtime CSV</CardTitle>
                <CardDescription>
                  Upload a CSV file with overtime hours for employees
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Template download */}
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <FileText size={24} className="text-slate-500" />
                    <div className="flex-1">
                      <p className="font-medium">CSV Template</p>
                      <p className="text-sm text-slate-500">
                        Download the template to see the required format
                      </p>
                    </div>
                    <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                      <Download size={16} className="mr-2" />
                      Download Template
                    </Button>
                  </div>

                  {/* File upload */}
                  <div>
                    <Label htmlFor="csv-file">Select CSV File</Label>
                    <div className="mt-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="csv-file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="block w-full text-sm text-slate-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-medium
                          file:bg-primary file:text-white
                          hover:file:bg-primary/90
                          cursor-pointer"
                        data-testid="csv-file-input"
                      />
                    </div>
                    <p className="mt-2 text-sm text-slate-500">
                      Required columns: <code className="bg-slate-100 px-1 rounded">email</code>, <code className="bg-slate-100 px-1 rounded">overtime_hours</code>
                      <br />
                      Optional: <code className="bg-slate-100 px-1 rounded">last_overtime_date</code> (YYYY-MM-DD format)
                    </p>
                  </div>

                  {/* Recent upload result */}
                  {uploadResult && (
                    <div className={`p-4 rounded-lg ${uploadResult.rows_failed > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {uploadResult.rows_failed > 0 ? (
                          <Warning size={20} className="text-amber-600" />
                        ) : (
                          <CheckCircle size={20} className="text-emerald-600" />
                        )}
                        <span className="font-medium">
                          Last Upload: {uploadResult.rows_processed} processed, {uploadResult.rows_failed} failed
                        </span>
                      </div>
                      {uploadResult.errors?.length > 0 && (
                        <div className="text-sm text-slate-600 mt-2">
                          <p className="font-medium mb-1">Errors:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {uploadResult.errors.slice(0, 5).map((err, idx) => (
                              <li key={idx}>Row {err.row}: {err.error}</li>
                            ))}
                            {uploadResult.errors.length > 5 && (
                              <li>...and {uploadResult.errors.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Snapshots Tab */}
        <TabsContent value="snapshots">
          <Card>
            <CardHeader>
              <CardTitle>Upload History</CardTitle>
              <CardDescription>
                Previous overtime uploads with the option to re-apply
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSnapshots ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : snapshots.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No upload history</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Uploaded At</TableHead>
                      <TableHead>Filename</TableHead>
                      <TableHead>Uploaded By</TableHead>
                      <TableHead>Rows Processed</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {snapshots.map((snapshot) => (
                      <TableRow key={snapshot.id} data-testid={`snapshot-row-${snapshot.id}`}>
                        <TableCell className="whitespace-nowrap">
                          {new Date(snapshot.uploaded_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {snapshot.source_filename}
                        </TableCell>
                        <TableCell>{snapshot.uploader_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {snapshot.rows_processed || 0} rows
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {snapshot.notes || <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReapply(snapshot.id)}
                            data-testid={`reapply-btn-${snapshot.id}`}
                          >
                            <ArrowClockwise size={16} className="mr-1" />
                            Re-apply
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Preview Upload</DialogTitle>
            <DialogDescription>
              Review the data before applying to user profiles
            </DialogDescription>
          </DialogHeader>

          {uploadPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">
                  File: <strong>{uploadPreview.file.name}</strong>
                </span>
                <Badge variant="secondary">
                  {uploadPreview.totalRows} rows
                </Badge>
              </div>

              <div className="max-h-64 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {uploadPreview.headers.map(h => (
                        <TableHead key={h}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploadPreview.rows.map((row, idx) => (
                      <TableRow key={idx}>
                        {uploadPreview.headers.map(h => (
                          <TableCell key={h}>{row[h]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {uploadPreview.totalRows > 10 && (
                <p className="text-sm text-slate-500 text-center">
                  Showing first 10 of {uploadPreview.totalRows} rows
                </p>
              )}

              <div>
                <Label htmlFor="upload-notes">Notes (optional)</Label>
                <Input
                  id="upload-notes"
                  value={uploadNotes}
                  onChange={(e) => setUploadNotes(e.target.value)}
                  placeholder="e.g., January 2026 overtime update"
                  className="mt-1"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="confirm-upload-btn">
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Apply Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOvertimePage;
